import React, { useState } from "react";
import { ChevronDown, ChevronUp, TrendingUp } from "lucide-react";
import { ProblematicPattern } from "../../../../../services/ai/types/ScanHistory.types";

interface PatternInsightsProps {
  patterns: ProblematicPattern[];
}

const FrequencyBadge = ({
  frequency
}: {
  frequency: number;
}): React.ReactElement => (
  <span
    className="inline-flex items-center rounded-full bg-[hsl(var(--warning)/0.15)] px-2 py-0.5 text-[11px] font-medium text-[hsl(var(--warning))]"
    aria-label={`Seen in ${frequency} sessions`}
  >
    {frequency} sessions
  </span>
);

const PatternItem = ({
  pattern
}: {
  pattern: ProblematicPattern;
}): React.ReactElement => (
  <div className="rounded-md bg-card p-2.5">
    <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
      <p className="flex-1 text-xs font-semibold text-foreground">
        {pattern.description}
      </p>
      <FrequencyBadge frequency={pattern.frequency} />
    </div>
    <p className="text-xs text-muted-foreground">{pattern.suggestion}</p>
  </div>
);

export const PatternInsights = ({
  patterns
}: PatternInsightsProps): React.ReactElement | null => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (patterns.length === 0) {
    return (
      <section
        className="my-3 rounded-lg border border-border p-3"
        aria-label="AI pattern insights"
      >
        <div className="flex items-center gap-2">
          <TrendingUp
            className="size-4 flex-shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
          <span className="text-sm font-semibold text-foreground">
            AI Insights
          </span>
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground">
          No patterns detected yet. Patterns will appear after multiple scan
          sessions.
        </p>
      </section>
    );
  }

  return (
    <section
      className="my-3 rounded-lg border border-border p-3"
      aria-label="AI pattern insights"
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
        <TrendingUp
          className="size-4 flex-shrink-0 text-[hsl(var(--warning))]"
          aria-hidden="true"
        />
        <span className="flex-1 text-sm font-semibold text-foreground">
          AI Insights: {patterns.length} pattern
          {patterns.length !== 1 ? "s" : ""} detected
        </span>
        {isExpanded ? (
          <ChevronUp
            className="size-4 flex-shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        ) : (
          <ChevronDown
            className="size-4 flex-shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        )}
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-2">
          {patterns.map((pattern, index) => (
            <PatternItem
              key={`${pattern.patternType}-${pattern.areaName ?? pattern.timeSlot ?? ""}-${index}`}
              pattern={pattern}
            />
          ))}
        </div>
      )}
    </section>
  );
};
